import React, { useRef, useState, useEffect } from 'react';
import { Share } from '@capacitor/share';

const font = "'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif";
const APP_URL = 'playbracketz.com';

// Category icons as text fallbacks (canvas-safe)
function getCategoryEmoji(category) {
  if (!category) return '🏆';
  const c = category.toLowerCase();
  if (/nba|nfl|mlb|nhl|sport|athlete|basketball|football|baseball|hockey|soccer|tennis|golf|olympic|boxing|mma/.test(c)) return '🏆';
  if (/music|song|album|artist|band|rapper|singer|rock|hip.hop|country/.test(c)) return '🎵';
  if (/movie|film|director|actor|actress|oscar|cinema/.test(c)) return '🎬';
  if (/tv|television|show|series|sitcom|streaming/.test(c)) return '📺';
  if (/food|pizza|burger|restaurant|bbq|taco|snack|candy|dessert/.test(c)) return '🍕';
  if (/game|gaming|nintendo|playstation|xbox/.test(c)) return '🎮';
  if (/president|historical|war|general|founding/.test(c)) return '📜';
  return '🏆';
}

// Draw rounded rect helper
function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

// Wrap text helper
function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  const words = text.split(' ');
  let line = '';
  let lines = [];
  for (const word of words) {
    const test = line + word + ' ';
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line.trim());
      line = word + ' ';
    } else {
      line = test;
    }
  }
  lines.push(line.trim());
  lines.forEach((l, i) => ctx.fillText(l, x, y + i * lineHeight));
  return lines.length;
}

export default function ShareCard({ bracket, userPicks, onClose, winRate, contrarianScore }) {
  const canvasRef   = useRef(null);
  const previewRef  = useRef(null);
  const [generating, setGenerating] = useState(false);
  const [previewReady, setPreviewReady] = useState(false);

  const champion = userPicks?.['r4_m0'] || bracket?.entries?.[0] || '???';
  const title    = bracket?.category || 'My Bracket';
  const emoji    = getCategoryEmoji(title);
  const shareText = `My champion pick for "${title}": ${champion}! Think you can beat me? Play Bracketz:`;

  const drawCard = (canvas) => {
    const ctx  = canvas.getContext('2d');
    const W    = 1080;
    const H    = 1350; // 4:5 ratio — perfect for Instagram
    canvas.width  = W;
    canvas.height = H;

    // ── BACKGROUND ────────────────────────────────────────────────────────────
    const bg = ctx.createLinearGradient(0, 0, 0, H);
    bg.addColorStop(0,   '#0B1928');
    bg.addColorStop(0.5, '#0F2035');
    bg.addColorStop(1,   '#0B1928');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    // Subtle grid pattern
    ctx.strokeStyle = 'rgba(255,255,255,0.025)';
    ctx.lineWidth = 1;
    for (let x = 0; x < W; x += 60) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
    for (let y = 0; y < H; y += 60) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }

    // Top orange accent bar
    const topBar = ctx.createLinearGradient(0, 0, W, 0);
    topBar.addColorStop(0, 'rgba(255,107,0,0)');
    topBar.addColorStop(0.3, '#FF6B00');
    topBar.addColorStop(0.7, '#FF6B00');
    topBar.addColorStop(1, 'rgba(255,107,0,0)');
    ctx.fillStyle = topBar;
    ctx.fillRect(0, 0, W, 6);

    // Bottom orange accent bar
    ctx.fillStyle = topBar;
    ctx.fillRect(0, H - 6, W, 6);

    // ── HEADER ────────────────────────────────────────────────────────────────
    ctx.textAlign = 'center';

    // BRACKETZ wordmark
    ctx.fillStyle = '#FF6B00';
    ctx.font = 'bold 72px Arial';
    ctx.letterSpacing = '8px';
    ctx.fillText('BRACKETZ', W / 2, 110);

    // Tagline
    ctx.fillStyle = 'rgba(255,255,255,0.35)';
    ctx.font = '28px Arial';
    ctx.letterSpacing = '3px';
    ctx.fillText('DAILY BRACKET CHALLENGE', W / 2, 155);

    // Divider
    const div = ctx.createLinearGradient(0, 0, W, 0);
    div.addColorStop(0, 'rgba(255,107,0,0)');
    div.addColorStop(0.5, 'rgba(255,107,0,0.4)');
    div.addColorStop(1, 'rgba(255,107,0,0)');
    ctx.strokeStyle = div;
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(80, 185); ctx.lineTo(W - 80, 185); ctx.stroke();

    // ── CATEGORY PILL ─────────────────────────────────────────────────────────
    ctx.letterSpacing = '0px';
    const pillW = 340, pillH = 52, pillX = (W - pillW) / 2, pillY = 210;
    roundRect(ctx, pillX, pillY, pillW, pillH, 26);
    ctx.fillStyle = 'rgba(255,107,0,0.12)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(255,107,0,0.35)';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    ctx.fillStyle = '#FF6B00';
    ctx.font = 'bold 22px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('TODAY\'S BRACKET', W / 2, 244);

    // ── BRACKET TITLE ─────────────────────────────────────────────────────────
    ctx.fillStyle = '#F0F4F8';
    ctx.font = 'bold 52px Arial';
    ctx.textAlign = 'center';
    const titleLines = wrapText(ctx, title, W / 2, 320, W - 160, 64);

    // ── CHAMPION SECTION ──────────────────────────────────────────────────────
    const champY = 320 + titleLines * 64 + 40;

    // Champion card background
    roundRect(ctx, 80, champY, W - 160, 340, 24);
    const champBg = ctx.createLinearGradient(80, champY, W - 80, champY + 340);
    champBg.addColorStop(0, 'rgba(255,215,0,0.08)');
    champBg.addColorStop(1, 'rgba(255,107,0,0.06)');
    ctx.fillStyle = champBg;
    ctx.fill();
    ctx.strokeStyle = 'rgba(255,215,0,0.35)';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // Trophy emoji
    const tX = W / 2, tY = champY + 65;
    ctx.font = '52px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('🏆', tX, tY);
    ctx.fillStyle = 'rgba(255,215,0,0.7)';
    ctx.font = 'bold 24px Arial';
    ctx.letterSpacing = '3px';
    ctx.textAlign = 'center';
    ctx.fillText('MY CHAMPION', W / 2, champY + 130);

    // Champion name
    ctx.letterSpacing = '0px';
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 68px Arial';
    const champLines = wrapText(ctx, champion, W / 2, champY + 230, W - 220, 76);

    // Seed if available
    ctx.fillStyle = 'rgba(255,215,0,0.5)';
    ctx.font = '24px Arial';
    ctx.fillText(emoji + '  ' + APP_URL, W / 2, champY + 200 + champLines * 76 + 20);

    // ── STATS ROW ─────────────────────────────────────────────────────────────
    const statsY = champY + 360;

    if (winRate !== null && winRate !== undefined || contrarianScore !== null && contrarianScore !== undefined) {
      // Stats background
      roundRect(ctx, 80, statsY, W - 160, 140, 16);
      ctx.fillStyle = 'rgba(255,255,255,0.03)';
      ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.08)';
      ctx.lineWidth = 1;
      ctx.stroke();

      // Divider line between stats
      ctx.strokeStyle = 'rgba(255,255,255,0.08)';
      ctx.beginPath(); ctx.moveTo(W / 2, statsY + 20); ctx.lineTo(W / 2, statsY + 120); ctx.stroke();

      // Win Rate
      if (winRate !== null && winRate !== undefined) {
        ctx.fillStyle = '#00C851';
        ctx.font = 'bold 52px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(winRate + '%', W / 4, statsY + 82);
        ctx.fillStyle = 'rgba(255,255,255,0.4)';
        ctx.font = '22px Arial';
        ctx.fillText('WIN RATE', W / 4, statsY + 116);
      }

      // Contrarian Score
      if (contrarianScore !== null && contrarianScore !== undefined) {
        ctx.fillStyle = '#F59E0B';
        ctx.font = 'bold 52px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(contrarianScore + '%', (W / 4) * 3, statsY + 82);
        ctx.fillStyle = 'rgba(255,255,255,0.4)';
        ctx.font = '22px Arial';
        ctx.fillText('CONTRARIAN', (W / 4) * 3, statsY + 116);
      }
    }

    // ── FOOTER ────────────────────────────────────────────────────────────────
    const footerY = H - 120;

    // QR-style CTA
    ctx.fillStyle = 'rgba(255,255,255,0.12)';
    ctx.font = '26px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Think you can pick better?', W / 2, footerY);

    ctx.fillStyle = '#FF6B00';
    ctx.font = 'bold 32px Arial';
    ctx.fillText('Play free at ' + APP_URL, W / 2, footerY + 44);

    // Hashtag
    ctx.fillStyle = 'rgba(255,255,255,0.25)';
    ctx.font = '24px Arial';
    ctx.fillText('#Bracketz  #BracketChallenge', W / 2, footerY + 82);
  };

  // Draw preview on mount
  useEffect(() => {
    if (previewRef.current) {
      drawCard(previewRef.current);
      setPreviewReady(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [champion, title]);

  const getBlob = () => new Promise(resolve => {
    const canvas = canvasRef.current;
    drawCard(canvas);
    canvas.toBlob(resolve, 'image/png');
  });

  const handleNativeShare = async () => {
    setGenerating(true);
    try {
      const blob = await getBlob();
      const file = new File([blob], 'my-bracketz-pick.png', { type: 'image/png' });
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ title: 'Bracketz', text: shareText + ' ' + APP_URL, files: [file] });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = 'my-bracketz-pick.png'; a.click();
        URL.revokeObjectURL(url);
      }
    } catch(e) { console.error(e); }
    setGenerating(false);
  };

  const handleDownload = async () => {
    setGenerating(true);
    const blob = await getBlob();
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a'); a.href = url; a.download = 'my-bracketz-pick.png'; a.click();
    URL.revokeObjectURL(url);
    setGenerating(false);
  };

  const twitterUrl  = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText + ' ' + APP_URL)}&hashtags=Bracketz`;
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent('https://' + APP_URL)}&quote=${encodeURIComponent(shareText)}`;

  const Cbg = '#0B1928';
  const Card = '#132030';
  const Bdr  = 'rgba(255,255,255,0.08)';
  const Org  = '#FF6B00';
  const Muted = '#5A7A9A';

  return (
    <div style={{ position:'fixed', inset:0, backgroundColor:'rgba(0,0,0,0.9)', zIndex:1000, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'flex-end', fontFamily:font }}>

      {/* Bottom sheet */}
      <div style={{ backgroundColor:Cbg, borderRadius:'20px 20px 0 0', width:'100%', maxWidth:'480px', maxHeight:'90vh', overflowY:'auto', WebkitOverflowScrolling:'touch' }}>

        {/* Handle */}
        <div style={{ width:'36px', height:'4px', borderRadius:'2px', background:'rgba(255,255,255,0.15)', margin:'12px auto 0' }}/>

        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'16px 20px 12px' }}>
          <div>
            <div style={{ fontSize:'16px', fontWeight:'800', color:'#F0F4F8' }}>Share Your Pick</div>
            <div style={{ fontSize: '16px', color:Muted, marginTop:'2px' }}>Save or post your bracket card</div>
          </div>
          <button onClick={onClose} style={{ backgroundColor:'rgba(255,255,255,0.06)', border:'none', borderRadius:'50%', width:'32px', height:'32px', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:Muted, fontSize:'16px' }}>✕</button>
        </div>

        {/* Live preview */}
        <div style={{ padding:'0 20px 16px' }}>
          <div style={{ borderRadius:'12px', overflow:'hidden', border:'1px solid rgba(255,107,0,0.3)', position:'relative' }}>
            <canvas
              ref={previewRef}
              style={{ width:'100%', height:'auto', display:'block' }}
            />
            {!previewReady && (
              <div style={{ position:'absolute', inset:0, backgroundColor:Card, display:'flex', alignItems:'center', justifyContent:'center' }}>
                <div style={{ color:Muted, fontSize: '16px' }}>Generating preview...</div>
              </div>
            )}
          </div>
        </div>

        {/* Share buttons */}
        <div style={{ padding:'0 20px 8px' }}>
          <div style={{ fontSize: '16px', fontWeight:'700', color:Muted, letterSpacing:'1.5px', textTransform:'uppercase', marginBottom:'10px' }}>Share to</div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'8px', marginBottom:'10px' }}>

            {/* X */}
            <a href={twitterUrl} target="_blank" rel="noopener noreferrer" style={{ backgroundColor:'#000', border:'1px solid #333', borderRadius:'12px', padding:'12px 8px', display:'flex', flexDirection:'column', alignItems:'center', gap:'5px', textDecoration:'none', cursor:'pointer' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              <span style={{ color:'#fff', fontSize: '16px', fontWeight:'700' }}>Post on X</span>
            </a>

            {/* Facebook */}
            <a href="#" onClick={(e) => { e.preventDefault(); handleNativeShare(); }} style={{ backgroundColor:'#1877F2', border:'none', borderRadius:'12px', padding:'12px 8px', display:'flex', flexDirection:'column', alignItems:'center', gap:'5px', textDecoration:'none', cursor:'pointer' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              <span style={{ color:'#fff', fontSize: '16px', fontWeight:'700' }}>Facebook</span>
            </a>

            {/* Instagram */}
            <button onClick={handleNativeShare} style={{ background:'linear-gradient(135deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)', border:'none', borderRadius:'12px', padding:'12px 8px', display:'flex', flexDirection:'column', alignItems:'center', gap:'5px', cursor:'pointer' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              <span style={{ color:'#fff', fontSize: '16px', fontWeight:'700' }}>Instagram</span>
            </button>
          </div>

          {/* Native share + Download row */}
          <div style={{ display:'flex', gap:'8px', marginBottom:'8px' }}>
            {typeof navigator !== 'undefined' && navigator.share && (
              <button onClick={handleNativeShare} disabled={generating} style={{ flex:1, backgroundColor:Org, border:'none', borderRadius:'12px', padding:'13px', fontSize: '16px', fontWeight:'700', color:'#fff', cursor:generating?'not-allowed':'pointer', fontFamily:font, opacity:generating?0.6:1 }}>
                {generating ? 'Generating...' : '↗ Share Image'}
              </button>
            )}
            <button onClick={handleDownload} disabled={generating} style={{ flex:1, backgroundColor:'rgba(255,255,255,0.06)', border:'1px solid ' + Bdr, borderRadius:'12px', padding:'13px', fontSize: '16px', fontWeight:'700', color:'#F0F4F8', cursor:generating?'not-allowed':'pointer', fontFamily:font, opacity:generating?0.6:1 }}>
              {generating ? '...' : '↓ Save Image'}
            </button>
          </div>

          {/* Hint for Instagram */}
          <div style={{ fontSize: '16px', color:Muted, textAlign:'center', marginBottom:'16px' }}>
            For Instagram — save the image then upload from your camera roll
          </div>
        </div>

        {/* Hidden full-res canvas */}
        <canvas ref={canvasRef} style={{ display:'none' }} />
      </div>
    </div>
  );
}
